import React, { useEffect, useState } from "react";
import { useClientInfo, SnackbarAlert } from "../../utils/commonImports";

// ================================================================
// TRIPSHEET SETTINGS FORM
// ================================================================
const TripsheetSettingsForm = ({ onSave }) => {
  const { clientId } = useClientInfo();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);

  const [form, setForm] = useState({
    document_title: "Tripsheet",
    duty_entry_type: "SINGLE",

    carry_forward_opening_km: false,
    multiple_others_entry: false,
    multiple_permit_entry: false,
    parking_others_permit_to_vehicle: false,

    check_vehicle_documents_expiry: false,
    check_tripsheets_returned: false,

    enable_print: true,
    template_id: null,
    print_client_name_address_vendor_vehicle: false,

    send_message_to_users: false,
    send_message_to_driver: false,
    send_message_to_vendor: false,

    terms_conditions: "",
  });

  // ================================================================
  // FETCH TRIPSHEET TEMPLATES
  // ================================================================
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);

        const res = await fetch(`/api/clients/${clientId}/tripsheet-templates`);

        if (!res.ok) {
          throw new Error("Failed to load Tripsheet templates");
        }

        const data = await res.json();

        setTemplates(Array.isArray(data?.templates) ? data.templates : []);
      } catch (err) {
        console.error("Error loading Tripsheet templates:", err);

        SnackbarAlert("Error loading Tripsheet templates", "error");
      } finally {
        setTemplatesLoading(false);
      }
    };

    if (clientId) {
      fetchTemplates();
    }
  }, [clientId]);

  // ================================================================
  // FETCH SETTINGS
  // ================================================================
  useEffect(() => {
    const fetchTripsheetSettings = async () => {
      try {
        const res = await fetch(`/api/clients/${clientId}/tripsheet-settings`);

        if (!res.ok) {
          throw new Error("Failed to load Tripsheet settings");
        }

        const data = await res.json();

        if (data?.tripsheet) {
          const t = data.tripsheet;

          setForm({
            document_title: t.document_title || "Tripsheet",

            duty_entry_type: t.duty_entry_type || "SINGLE",

            carry_forward_opening_km: t.carry_forward_opening_km ?? false,

            multiple_others_entry: t.multiple_others_entry ?? false,

            multiple_permit_entry: t.multiple_permit_entry ?? false,

            parking_others_permit_to_vehicle:
              t.parking_others_permit_to_vehicle ?? false,

            check_vehicle_documents_expiry:
              t.check_vehicle_documents_expiry ?? false,

            check_tripsheets_returned: t.check_tripsheets_returned ?? false,

            enable_print: t.enable_print ?? true,

            template_id: t.template_id ?? null,

            print_client_name_address_vendor_vehicle:
              t.print_client_name_address_vendor_vehicle ?? false,

            send_message_to_users: t.send_message_to_users ?? false,

            send_message_to_driver: t.send_message_to_driver ?? false,

            send_message_to_vendor: t.send_message_to_vendor ?? false,

            terms_conditions: t.terms_conditions || "",
          });
        }
      } catch (err) {
        console.error("Error loading Tripsheet settings:", err);

        SnackbarAlert("Error loading Tripsheet settings", "error");
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      fetchTripsheetSettings();
    }
  }, [clientId]);

  // ================================================================
  // FIELD CHANGE
  // ================================================================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ================================================================
  // TOGGLE
  // ================================================================
  const handleToggle = (name) => {
    setForm((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  // ================================================================
  // SAVE
  // ================================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = {
        ...form,
        template_id: form.template_id === "" ? null : form.template_id,
      };

      const res = await fetch(`/api/clients/${clientId}/tripsheet-settings`, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Failed to save Tripsheet settings");
      }

      SnackbarAlert(
        data.message || "Tripsheet settings saved successfully",
        "success",
      );

      if (onSave) {
        onSave(payload);
      }
    } catch (err) {
      console.error("TRIPSHEET SAVE ERROR:", err);

      SnackbarAlert(err.message || "Error saving Tripsheet settings", "error");
    } finally {
      setSaving(false);
    }
  };

  // ================================================================
  // LOADING
  // ================================================================
  if (loading) {
    return (
      <div className="w-full flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />

          <p className="mt-3 text-sm text-gray-500">
            Loading Tripsheet settings...
          </p>
        </div>
      </div>
    );
  }

  // ================================================================
  // UI
  // ================================================================
  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-5xl bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
    >
      {/* ==========================================================
          HEADER
      =========================================================== */}
      <div className="px-7 py-5 border-b border-gray-200 bg-gradient-to-r from-white to-blue-50/40">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700">
                <span className="text-lg">📋</span>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Tripsheet Settings
                </h2>

                <p className="text-xs text-gray-500 mt-0.5">
                  Configure how Tripsheets are created, controlled, printed and
                  communicated.
                </p>
              </div>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-100">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-xs font-medium text-green-700">
              Client Settings
            </span>
          </div>
        </div>
      </div>

      <div className="p-7 space-y-7">
        {/* ========================================================
            GENERAL
        ========================================================= */}
        <SettingsSection
          number="01"
          title="General"
          description="Define the basic Tripsheet document settings."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Tripsheet Title">
              <input
                type="text"
                name="document_title"
                value={form.document_title}
                onChange={handleChange}
                placeholder="Tripsheet"
                maxLength={100}
                // className={inputClass}
                                className="w-full rounded-lg border border-gray-300 px-3 py-3 text-lg font-bold text-gray-800 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"

              />

              <FieldHint>
                This title will appear on the Tripsheet and printed document.
              </FieldHint>
            </Field>
          </div>
        </SettingsSection>

        {/* ========================================================
            DUTY ENTRY
        ========================================================= */}
        <SettingsSection
          number="02"
          title="Duty Entry"
          description="Control how duties are entered in a Tripsheet."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Duty Entry Type */}
            <div className="md:col-span-2">
              <Field label="Duty Entry in Tripsheet">
                <div className="relative">
                  <select
                    name="duty_entry_type"
                    value={form.duty_entry_type}
                    onChange={handleChange}
                    className={`${inputClass} appearance-none pr-10`}
                  >
                    <option value="SINGLE">Single Duty</option>

                    <option value="MULTIPLE">Multiple Duties</option>
                  </select>

                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    ▼
                  </span>
                </div>

                <FieldHint>
                  Choose whether one or multiple duties can be entered in a
                  single Tripsheet.
                </FieldHint>
              </Field>
            </div>

            <ToggleRow
              label="Carry Forward Opening KM"
              description="Use the previous closing KM as the next opening KM."
              checked={form.carry_forward_opening_km}
              onChange={() => handleToggle("carry_forward_opening_km")}
            />

            <ToggleRow
              label="Multiple Others Entry"
              description="Allow more than one Others entry."
              checked={form.multiple_others_entry}
              onChange={() => handleToggle("multiple_others_entry")}
            />

            <ToggleRow
              label="Multiple Permit Entry"
              description="Allow more than one Permit entry."
              checked={form.multiple_permit_entry}
              onChange={() => handleToggle("multiple_permit_entry")}
            />

            <ToggleRow
              label="Parking, Others & Permit Values to Vehicle"
              description="Store these values against the vehicle."
              checked={form.parking_others_permit_to_vehicle}
              onChange={() => handleToggle("parking_others_permit_to_vehicle")}
            />
          </div>
        </SettingsSection>

        {/* ========================================================
            VEHICLE CONTROLS
        ========================================================= */}
        <SettingsSection
          number="03"
          title="Vehicle Controls"
          description="Set checks that should be performed during Tripsheet processing."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ToggleRow
              label="Check Vehicle Documents Expiry"
              description="Check vehicle document expiry during Tripsheet processing."
              checked={form.check_vehicle_documents_expiry}
              onChange={() => handleToggle("check_vehicle_documents_expiry")}
            />

            <ToggleRow
              label="Check Tripsheets Returned"
              description="Check whether previous Tripsheets have been returned."
              checked={form.check_tripsheets_returned}
              onChange={() => handleToggle("check_tripsheets_returned")}
            />
          </div>
        </SettingsSection>

        {/* ========================================================
            PRINTING
        ========================================================= */}
        <SettingsSection
          number="04"
          title="Printing"
          description="Control Tripsheet printing and the selected document format."
        >
          <div className="space-y-4">
            <ToggleRow
              label="Enable Tripsheet Print Option"
              description="Allow users to print Tripsheets."
              checked={form.enable_print}
              onChange={() => handleToggle("enable_print")}
            />

            {form.enable_print && (
              <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700">
                    🖨️
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">
                      Tripsheet Format
                    </h4>

                    <p className="text-xs text-gray-500 mt-0.5">
                      Select the common document template for this client's
                      Tripsheet.
                    </p>
                  </div>
                </div>

                <Field label="Tripsheet Template">
                  <div className="relative">
                    <select
                      name="template_id"
                      value={form.template_id ?? ""}
                      onChange={handleChange}
                      disabled={templatesLoading}
                      className={`${inputClass} appearance-none pr-10 disabled:bg-gray-100 disabled:text-gray-400`}
                    >
                      <option value="">
                        {templatesLoading
                          ? "Loading templates..."
                          : templates.length === 0
                            ? "No Tripsheet templates available"
                            : "Select Tripsheet Template"}
                      </option>

                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.template_name}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      ▼
                    </span>
                  </div>

                  <FieldHint>
                    The selected template will be used for both Tripsheet
                    preview and printing.
                  </FieldHint>
                </Field>
              </div>
            )}

            {form.enable_print && (
              <ToggleRow
                label="Print Client Name & Address for Vendor Vehicle"
                description="Print the client's name and address when a vendor vehicle is used."
                checked={form.print_client_name_address_vendor_vehicle}
                onChange={() =>
                  handleToggle("print_client_name_address_vendor_vehicle")
                }
              />
            )}
          </div>
        </SettingsSection>

        {/* ========================================================
            MESSAGING
        ========================================================= */}
        <SettingsSection
          number="05"
          title="Messaging"
          description="Choose who can receive Tripsheet messages."
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ToggleRow
              label="Users"
              description="Enable messaging to system users."
              checked={form.send_message_to_users}
              onChange={() => handleToggle("send_message_to_users")}
            />

            <ToggleRow
              label="Driver"
              description="Enable messaging to the driver."
              checked={form.send_message_to_driver}
              onChange={() => handleToggle("send_message_to_driver")}
            />

            <ToggleRow
              label="Vendor"
              description="Enable messaging to the vendor."
              checked={form.send_message_to_vendor}
              onChange={() => handleToggle("send_message_to_vendor")}
            />
          </div>
        </SettingsSection>

        {/* ========================================================
            TERMS & CONDITIONS
        ========================================================= */}
        <SettingsSection
          number="06"
          title="Terms & Conditions"
          description="Define the standard terms that can appear on the Tripsheet."
        >
          <Field label="Tripsheet Terms & Conditions">
            <textarea
              name="terms_conditions"
              value={form.terms_conditions}
              onChange={handleChange}
              rows={7}
              placeholder="Enter standard Tripsheet terms & conditions..."
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 outline-none transition resize-y focus:border-blue-500 focus:ring-2 focus:ring-blue-100 placeholder:text-gray-300"
            />

            <FieldHint>
              These terms can be used when generating or printing the Tripsheet.
            </FieldHint>
          </Field>
        </SettingsSection>
      </div>

      {/* ==========================================================
          SAVE FOOTER
      =========================================================== */}
      <div className="px-7 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500" />

          <span className="text-xs text-gray-500">
            Settings are saved for the current client.
          </span>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 min-w-[150px] px-6 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
        >
          {saving ? (
            <>
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <span>✓</span>
              Save Changes
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// ================================================================
// SETTINGS SECTION
// ================================================================
const SettingsSection = ({ number, title, description, children }) => {
  return (
    <section>
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold">
          {number}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>

          {description && (
            <p className="text-xs text-gray-400 mt-0.5">{description}</p>
          )}
        </div>
      </div>

      <div className="pl-0 md:pl-11">{children}</div>
    </section>
  );
};

// ================================================================
// FIELD
// ================================================================
const Field = ({ label, children }) => {
  return (
    <div className="min-w-0">
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
        {label}
      </label>

      {children}
    </div>
  );
};

// ================================================================
// FIELD HINT
// ================================================================
const FieldHint = ({ children }) => {
  return (
    <p className="text-[11px] text-gray-400 mt-1.5 leading-relaxed">
      {children}
    </p>
  );
};

// ================================================================
// TOGGLE ROW
// ================================================================
const ToggleRow = ({ label, description, checked, onChange }) => {
  return (
    <div
      className={`
        group
        flex items-center justify-between
        gap-4
        min-h-[76px]
        rounded-lg
        border
        px-4 py-3
        transition-all
        ${
          checked
            ? "border-blue-200 bg-blue-50/50"
            : "border-gray-200 bg-white hover:border-gray-300"
        }
      `}
    >
      <div className="min-w-0">
        <div
          className={`
            text-sm font-semibold
            ${checked ? "text-blue-800" : "text-gray-700"}
          `}
        >
          {label}
        </div>

        {description && (
          <div className="text-xs text-gray-400 mt-1 leading-relaxed">
            {description}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onChange}
        aria-pressed={checked}
        aria-label={`${label} ${checked ? "enabled" : "disabled"}`}
        className={`
          relative flex-shrink-0
          w-11 h-6
          rounded-full
          transition-colors
          focus:outline-none
          focus:ring-2
          focus:ring-blue-200
          ${checked ? "bg-blue-600" : "bg-gray-300"}
        `}
      >
        <span
          className={`
            absolute
            top-0.5
            left-0.5
            w-5
            h-5
            rounded-full
            bg-white
            shadow-sm
            transition-transform
            ${checked ? "translate-x-5" : "translate-x-0"}
          `}
        />
      </button>
    </div>
  );
};

// ================================================================
// INPUT STYLE
// ================================================================
const inputClass =
  "w-full h-10 rounded-lg border border-gray-300 bg-white px-3.5 text-sm text-gray-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 placeholder:text-gray-300";

export default TripsheetSettingsForm;
